import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Modal from "react-modal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useUpdateEffect } from "react-use";
import { useRecoilState, useResetRecoilState } from "recoil";
import SurveyForm from "../../components/survey/SurveyForm";
import { getSurvey, responseSurvey } from "../../api";
import {
  surveyResponseTime,
  surveyInfoContents,
  surveyInfoTitle,
  surveyResponseList,
} from "../../states/atoms/surveyState";
import { Alert, Button, ErrorAlert } from "../../components/common/Forms";
import { MetaTag, useLocationQuery } from "../../util/common";

const SurveyForPage = () => {
  const query = useLocationQuery();
  const location = useLocation();
  const { target, user_id } = query;
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState(false);
  const [title, setTitle] = useRecoilState(surveyInfoTitle);
  const [contents, setContents] = useRecoilState(surveyInfoContents);
  const [list, setList] = useState(null);
  const [responses, setResponseList] = useRecoilState(surveyResponseList);
  const [time, setTime] = useRecoilState(surveyResponseTime);
  const resetTime = useResetRecoilState(surveyResponseTime);
  const resetResponse = useResetRecoilState(surveyResponseList);
  const [btnDisabled, setBtnStatus] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    loading: false,
    result: null,
    error: false,
  });

  const {
    status,
    data,
    error: err,
  } = useQuery({
    queryKey: [`survey/${target}`, id],
    queryFn: () => getSurvey({ id, target }),
  });
  const surveyMttn = useMutation({
    mutationFn: responseSurvey,
    onSettled: async (data, error) => {
      setModal({ ...modal, error: false });
      if (data.result) {
        setModal({
          isOpen: true,
          loading: false,
          result: "설문 응답이 완료되었습니다.",
          error: false,
        });
      } else {
        readOnly(false);
        setModal({
          isOpen: true,
          loading: false,
          result: error,
          error: true,
        });
      }
      queryClient.invalidateQueries({
        queryKey: [`survey/${target}`, id],
      });
    },
  });

  const handleChange = (index, content) => {
    if (!time) {
      setTime(new Date().valueOf());
    }
    const newRes = [...responses];
    newRes.splice(index, 1, content);
    setResponseList(newRes);
  };

  const handleSave = () => {
    setReadOnly(true);
    const requiredTime = new Date().valueOf() - time;
    const obj = {
      id,
      target,
      responses,
      requiredTime,
    };
    if (user_id) {
      obj.user_id = user_id || "";
    }
    surveyMttn.mutate(obj);
    resetTime();
  };

  const handleClose = () => {
    setModal({
      isOpen: false,
      loading: false,
      result: null,
      error: false,
    });
    if (!modal.error) {
      setList([]);
      resetResponse();
      window.close();
    }
  };

  useUpdateEffect(() => {
    const v =
      list &&
      list.every(({ q: { required } }, i) => {
        const item = responses[i];
        if (typeof item === "object" && Array.isArray(item)) {
          return !required || (required && item.length > 0);
        }
        return !required || (required && !!responses[i]);
      });
    setBtnStatus(!v);
  }, [responses]);

  useUpdateEffect(() => {
    if (status === "success" && data.result) {
      if (data.data) {
        setError(false);
        setTitle(data.data.title);
        setContents(data.data.contents);
        const tmpList = data.data.questions.reduce((acc, cur, i) => {
          acc.push({
            q: cur,
            option: data.data.options[i],
          });
          return acc;
        }, []);
        setList(tmpList);
        setResponseList(new Array(tmpList.length).fill(null));
      } else {
        setError(
          `해당 강의에 ${
            target === "parent" ? "학부모 " : ""
          }설문조사가 존재하지 않습니다.`
        );
      }
    } else {
      setError(
        `${
          target === "parent" ? "학부모 " : ""
        }설문조사 로드 중 문제가 발생하였습니다.`
      );
    }
  }, [status]);

  useUpdateEffect(() => {
    setError(err || false);
  }, [err]);

  return (
    <>
      <MetaTag
        title={title}
        url={`https://themaker.circul.us/${location.pathname}`}
        imgsrc="https://themaker.circul.us/meta_img.png"
        description={contents.join(" ")}
      />
      <div className="overflow-hidden bg-zinc-100">
        {list && (
          <SurveyForm
            {...{
              id,
              title,
              contents,
              error,
              list,
              responses,
              handleChange,
              handleSave,
              btnDisabled,
            }}
          />
        )}
        {!list && error && (
          <div className="container mx-auto lg:my-8 lg:max-w-screen-lg flex justify-center">
            <ErrorAlert>{error}</ErrorAlert>
          </div>
        )}
        <Modal
          className="h-full text-center flex"
          isOpen={modal.isOpen}
          ariaHideApp={false}
        >
          <div className="card w-1/2 bg-white shadow-xl m-auto">
            <div className="card-body overflow-hidden gap-4">
              {!modal.loading && modal.result && <Alert>{modal.result}</Alert>}
              {modal.loading && (
                <progress className="progress progress-primary w-full"></progress>
              )}
              {modal.error && <ErrorAlert>{modal.result}</ErrorAlert>}
            </div>
            <div className="card-actions justify-center">
              <Button
                type="primary"
                className="rounded-t-none"
                onClick={handleClose}
                block
              >
                확인
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default SurveyForPage;
